<template>
  <div class="vertical-tree-cascader">
    <div class="cascader-trigger" @click="togglePanel">
      <span :class="displayClass">{{ displayText }}</span>
      <span class="cascader-arrow" :class="{ expanded: isOpen }"
        ><i
          class="el-icon-close"
          v-if="selectedValue"
          @click="clearSelection"
        ></i
        ><i class="el-icon-arrow-down" v-else style="font-size: 14px"></i
      ></span>
    </div>

    <div class="cascader-panel" v-show="isOpen">
      <div class="search-container">
        <input
          v-if="flowId"
          type="text"
          class="search-input"
          placeholder="搜索..."
          v-model="searchKeyword"
          @input="expandMatchingNodes"
        />
        <div v-else class="font-12 text-center text-gray">请先选择流程</div>
      </div>

      <div class="tree-container" v-if="flowId">
        <div v-if="!hasSearchResults()" class="no-results">
          未找到匹配的节点
        </div>
        <template v-else>
          <StepNode
            v-for="node in lists"
            :key="node.value"
            :node="node"
            :level="0"
            :selected-value="selectedValue"
            :expanded-nodes="expandedNodes"
            :search-keyword="searchKeyword"
            @select="selectNode"
            @toggle="toggleNode"
          >
          </StepNode>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { serviceQuestFlowStepList } from '@/service/getData'
import StepNode from './StepNode.vue'
export default {
  components: {
    StepNode,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    flowId: {
      type: Number,
      default: 0,
    },
    showAllChildSelect: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    flowId() {
      this.requestData()
      this.clearSelection()
    },
  },
  data() {
    return {
      lists: [],
      isOpen: false,
      selectedValue: null,
      selectedLabel: '',
      expandedNodes: [],
      searchKeyword: '',
    }
  },
  created() {
    this.requestData()
  },
  methods: {
    async requestData() {
      if (!this.flowId) {
        this.lists = []
        return
      }
      let res = await serviceQuestFlowStepList({
        service_quest_flow_id: this.flowId,
      })
      if (res.code == 0) {
        this.lists = this.arrayToTree(res.data.list, 0)
      }
    },
    arrayToTree(list, root) {
      let result = [] // 用于存放结果
      const map = {} // 用于存放 list 下的节点

      // 1. 遍历 list，将 list 下的所有节点以 id 作为索引存入 map
      for (const item of list) {
        map[item.id] = { label: item.name, value: item.id } // 浅拷贝
      }

      // 2. 再次遍历，将根节点放入最外层，子节点放入父节点
      for (const item of list) {
        // 3. 获取节点的 id 和 父 id
        const { id, father_id } = item // ES6 解构赋值
        // 4. 如果是根节点，存入 result
        if (item.father_id === root) {
          if (this.showAllChildSelect) {
            result.push({
              label: '所有子流程',
              value: father_id,
            })
          }
          result.push(map[id])
        } else {
          if (!map[father_id].children) {
            map[father_id].children = []
            if (this.showAllChildSelect) {
              map[father_id].children.push({
                label: '所有子流程',
                value: 0,
              })
            }
          }
          map[father_id].children.push(map[id])
        }
      }

      if (!this.showAllChildSelect) {
        result = [{ label: '评估', value: 0, children: result }]
      }
      // 将结果返回
      return result
    },

    togglePanel() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus()
        })
      }
    },

    closePanel() {
      this.isOpen = false
    },

    selectNode(node) {
      this.selectedValue = node.value
      this.selectedLabel = node.label
      this.$emit('input', [node.value])
      this.$emit('change')
      this.togglePanel()
    },

    toggleNode(node) {
      const index = this.expandedNodes.indexOf(node.value)
      if (index > -1) {
        this.expandedNodes.splice(index, 1)
      } else {
        this.expandedNodes.push(node.value)
      }
    },

    clearSelection() {
      this.selectedValue = null
      this.selectedLabel = ''
      this.$emit('input', [])
      this.$emit('change')
    },

    updateSelectedLabel() {
      if (this.selectedValue) {
        const node = this.findNode(this.lists, this.selectedValue)
        if (node) {
          this.selectedLabel = node.label
        }
      } else {
        this.selectedLabel = ''
      }
    },

    findNode(nodes, value) {
      for (const node of nodes) {
        if (node.value === value) {
          return node
        }
        if (node.children) {
          const found = this.findNode(node.children, value)
          if (found) return found
        }
      }
      return null
    },

    findPath(nodes, targetValue, path = []) {
      for (const node of nodes) {
        if (node.value === targetValue) {
          return [...path, node.label]
        }
        if (node.children) {
          const childPath = this.findPath(node.children, targetValue, [
            ...path,
            node.label,
          ])
          if (childPath) return childPath
        }
      }
      return null
    },

    expandMatchingNodes() {
      if (!this.searchKeyword) return

      // 搜索时展开所有包含匹配项的路径
      const expandMatchingPaths = (nodes) => {
        nodes.forEach((node) => {
          const isMatch = node.label
            .toLowerCase()
            .includes(this.searchKeyword.toLowerCase())
          const hasMatchingChildren = this.hasMatchingChildren(node)

          if (isMatch || hasMatchingChildren) {
            if (!this.expandedNodes.includes(node.value)) {
              this.expandedNodes.push(node.value)
            }
          }

          if (node.children) {
            expandMatchingPaths(node.children)
          }
        })
      }

      expandMatchingPaths(this.lists)
    },

    hasMatchingChildren(node) {
      if (!node.children) return false

      for (const child of node.children) {
        if (
          child.label
            .toLowerCase()
            .includes(this.searchKeyword.toLowerCase()) ||
          this.hasMatchingChildren(child)
        ) {
          return true
        }
      }

      return false
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closePanel()
      }
    },

    hasSearchResults() {
      if (!this.searchKeyword) return true

      const checkNodes = (nodes) => {
        for (const node of nodes) {
          if (
            node.label
              .toLowerCase()
              .includes(this.searchKeyword.toLowerCase()) ||
            (node.children && checkNodes(node.children))
          ) {
            return true
          }
        }
        return false
      }

      return checkNodes(this.lists)
    },
  },
  computed: {
    displayText() {
      return this.selectedLabel || '请选择'
    },

    displayClass() {
      return this.selectedLabel ? 'selected-value' : 'placeholder'
    },

    hasSelection() {
      return !!this.selectedValue
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.updateSelectedLabel()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
}
</script>

<style>
.vertical-tree-cascader {
  width: 220px;
  display: flex;
  align-items: center;
  height: 40px;
}

.cascader-trigger {
  min-width: 100%;
  height: 32px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fff;
  font-size: 13px;
}

.cascader-trigger:hover {
  border-color: #c0c4cc;
}

.placeholder {
  color: #c0c4cc;
  font-size: 13px;
}

.selected-value {
  color: #303133;
}

.cascader-arrow {
  margin-left: auto;
  transition: transform 0.3s;
  color: #c0c4cc;
  font-size: 12px;
}

.cascader-arrow.expanded {
  transform: rotate(180deg);
}

.cascader-panel {
  position: absolute;
  top: 100%;
  width: max-content;
  left: 0;
  min-width: 220px;
  margin-top: 12px;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
  padding: 0;
}

.search-container {
  padding: 4px 10px;
  border-bottom: 1px solid #e4e7ed;
}

.search-input {
  width: 100%;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.tree-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px 0;
}

.tree-node {
  position: relative;
  line-height: 28px;
}

.tree-node-item {
  display: flex;
  align-items: center;
  padding: 2px 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.tree-node-item:hover {
  background-color: #f5f7fa;
}

.tree-node-item.selected {
  /* color: #409eff; */
  font-weight: 500;
  /* background-color: #f0f7ff; */
}

.tree-node-item.highlight {
  /* background-color: #fff9e6; */
}

.checkbox-container {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.custom-checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.custom-checkbox.checked {
  background-color: #409eff;
  border-color: #409eff;
}

.custom-checkbox.checked::after {
  content: '✓';
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.expand-icon {
  margin-right: 8px;
  transition: transform 0.3s;
  color: #909399;
  font-size: 12px;
  width: 16px;
  text-align: center;
  cursor: pointer;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.node-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.node-label {
  flex: 1;
  /* line-height: 35px; */
}

.highlight-text {
  color: #e6a23c;
  font-weight: 600;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.selected-path {
  margin-top: 10px;
  font-size: 14px;
  color: #409eff;
  padding: 12px;
  background-color: #f0f7ff;
  border-radius: 4px;
}

.panel-footer {
  padding: 12px 16px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: #f8f9fa;
}

.action-button {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: white;
  color: #606266;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  margin-left: 8px;
}

.action-button:hover {
  background-color: #f5f7fa;
}

.action-button.primary {
  background-color: #409eff;
  color: white;
  border-color: #409eff;
}

.action-button.primary:hover {
  background-color: #66b1ff;
}

.features {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;
}

.feature-card {
  flex: 1;
  min-width: 200px;
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  border-top: 3px solid #409eff;
}

.feature-card h4 {
  color: #303133;
  margin-bottom: 8px;
  font-size: 16px;
}

.feature-card p {
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.demo-description {
  margin-top: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
  border-radius: 8px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  border-left: 4px solid #409eff;
}

.demo-description h3 {
  color: #303133;
  margin-bottom: 12px;
  font-size: 18px;
}

.demo-description ul {
  padding-left: 20px;
  margin: 10px 0;
}

.demo-description li {
  margin-bottom: 8px;
}
</style>